/**
 * TG-6 (AC-16, AC-17): the name and email validation the Assign and
 * Reassign dialogs share, with H2's two-level wording: a short line in
 * the error summary and the longer "…, like name@example.com" line
 * beside the email field.
 */
import type { ErrorSummaryItem } from "../../design-system";
import { getErrorMessage } from "../../validation/error-registry";
import { renewalCaptureContent as content } from "./content";

export interface AssigneeFieldErrors {
  readonly summary: readonly ErrorSummaryItem[];
  readonly nameError?: string;
  readonly emailError?: string;
}

/** The assignee-name length ceiling (`src/api/functions/assignments.py`,
 * AC-17) applied client-side, after trimming. Not exported (ADV-18): no
 * consumer outside this module ever needed it. */
const ASSIGNEE_NAME_MAX_LENGTH = 100;

/** FIND-1207: a Unicode format character (category `Cf` -- the zero-width
 * space U+200B-U+200D, the word joiner U+2060, the byte-order mark/
 * zero-width no-break space U+FEFF, and others) is invisible but still
 * splits a literal substring like "http" so it no longer matches, and any
 * whitespace character other than the ordinary space U+0020 (a
 * non-breaking space, a tab, or another Unicode separator) does the same
 * inside a digit run. OBJ-S-4/OBJ-R-2 (FIND-1233, FIND-1221 re-raise):
 * `Cn` (unassigned -- fails closed on a mark this runtime's Unicode table
 * does not yet classify, closing the version-skew gap between this ICU
 * build and the server's `unicodedata` table), `Co` (private use) and `Cs`
 * (surrogate) are refused the same way, plus the four Default_Ignorable
 * code points outside those categories: the Hangul fillers U+115F, U+1160,
 * U+3164, U+FFA0 (category `Lo`) and the Braille pattern blank U+2800
 * (category `So`), which renders as empty space exactly like an ordinary
 * space inside "ht tp". JavaScript's `\s` does not match U+0085 (NEL) or
 * U+001C-U+001F (the information separators), which Python's `str.isspace()`
 * treats as whitespace server-side; every control character (`Cc`, which covers
 * U+001C-U+001F) is refused on both sides, and U+0085 is matched explicitly, to keep
 * the two rules in parity (OBJ-R-5). Matches any. */
const DISALLOWED_CHARACTER_PATTERN = /\p{Cc}|\p{Cf}|\p{Cn}|\p{Co}|\p{Cs}|[ᅟᅠㅤﾠ⠀\u0085]|[^\S ]/u;

/** FIND-1207: the separator characters a phone number is commonly broken
 * up with -- stripped out before the digit-run check runs, so
 * "071 23 45 678" (spaces), "071-23-45-678" (hyphens), "071.23.45.678"
 * (dots) and "(071) 23 45 678" (parentheses) are all still read as one run
 * of digits. */
const PHONE_RUN_SEPARATOR_PATTERN = /[\s().-]/gu;

/** AC-17 (FIND-1215, FIND-1217, FIND-1221, OBJ-S-1/OBJ-R-2): a literal code-point comparison
 * against 'http'/'www.'/a digit run is bypassed by a compatibility-equivalent confusable --
 * fullwidth letters and digits, circled or superscript digits, or a combining mark stitched onto
 * an otherwise-innocuous letter. NFKD -- compatibility *decomposition*, deliberately not NFKC --
 * folds fullwidth, circled and superscript forms to their plain-ASCII equivalents and splits an
 * accented letter back into its bare base letter plus a separate combining mark; NFKC would
 * instead recompose a base letter immediately followed by a combining mark (e.g. 'w' + U+0301)
 * into a single precomposed accented letter (U+1E83 'ẃ'), which is not in category `Mn` and so
 * would survive the strip below untouched. Dropping every combining mark (category `Mn`) after
 * NFKD therefore leaves the bare base letters and digits behind, so 'hｔtp' and 'h́ttp'
 * both read as 'http'. Used only to decide whether to refuse -- never to change what is
 * displayed or sent to the server. */
const COMBINING_MARK_PATTERN = /\p{Mn}/gu;

/** OBJ-S-3 (FIND-1226): IDNA/UTS#46 dot-equivalents that NFKD does not fold to ASCII `.` --
 * the ideographic full stop U+3002, the halfwidth ideographic full stop U+FF61, and the
 * fullwidth full stop U+FF0E (already folded by NFKD, mapped here too for symmetry with the
 * server). `new URL('http://www。evil.example/').hostname` resolves to `www.evil.example`,
 * so a name containing one of these reads as a real, navigable address even though it is none
 * of `Mn`, `Cf`, or whitespace and so passes the strip above untouched. Mapped to `.` only for
 * the refusal check below -- never applied to the displayed or submitted name. */
const DOT_EQUIVALENT_PATTERN = /[。｡．]/gu;

function confusableFree(trimmedName: string): string {
  return trimmedName
    .normalize("NFKD")
    .replace(COMBINING_MARK_PATTERN, "")
    .replace(DOT_EQUIVALENT_PATTERN, ".");
}

/** AC-17: the same name rule `api.functions.assignments` applies
 * server-side, applied client-side so the Assign and Reassign dialogs
 * refuse before a round trip rather than only after a 422. A trimmed,
 * non-empty name is rejected when it is over 100 characters, contains a
 * line break, a Unicode format character, any whitespace other than an
 * ordinary space, an '@', 'http' or 'www.' (any case, ignoring any
 * whitespace between its letters), or a run of 5 or more digits once any
 * spaces, hyphens, dots or parentheses between them are ignored
 * (FIND-1207), checked against the NFKD-normalised, combining-mark-stripped
 * form so a confusable cannot slip these checks (OBJ-S-1/OBJ-R-2, ADV-12). An empty
 * name is handled separately (`missing`, above) -- this checks only the
 * OTHER rejection reasons. */
export function isAssigneeNameRejectedByFormatRule(trimmedName: string): boolean {
  // OBJ-C-10 (FIND-1237): `.length` counts UTF-16 code units, so a name built
  // from supplementary-plane characters (each a surrogate pair) is refused
  // here at half the server's real limit while `len(name)` in
  // `api.functions.assignments` counts Unicode code points. The spread
  // operator iterates by code point, matching the server exactly.
  if ([...trimmedName].length > ASSIGNEE_NAME_MAX_LENGTH) {
    return true;
  }
  if (/[\r\n]/.test(trimmedName)) {
    return true;
  }
  if (DISALLOWED_CHARACTER_PATTERN.test(trimmedName)) {
    return true;
  }
  const checkValue = confusableFree(trimmedName);
  const withoutSpaces = checkValue.toLowerCase().replace(/\s/gu, "");
  if (checkValue.includes("@") || withoutSpaces.includes("http") || withoutSpaces.includes("www.")) {
    return true;
  }
  const digitsOnly = checkValue.replace(PHONE_RUN_SEPARATOR_PATTERN, "");
  // OBJ-R-5: `\d` in a JS regex matches ASCII digits only, while Python's `\d` (used server-side)
  // matches any Unicode decimal-digit character (`Nd`) -- Arabic-Indic, Devanagari, Bengali and
  // every other `Nd` block. `\p{Nd}` keeps the two digit-run checks in parity.
  return /\p{Nd}{5,}/u.test(digitsOnly);
}

/** The assignee-email length ceiling (`src/api/functions/assignments.py`, `_EMAIL_MAX_LENGTH`)
 * applied client-side (ADV-10): without it, a 321+ character address reaches the server, which
 * refuses it with a 422 the dialogs map to the generic "Something went wrong" message instead of
 * the existing, more useful invalid-format copy `isSyntacticallyValidEmail`'s other refusals
 * already show. */
export const ASSIGNEE_EMAIL_MAX_LENGTH = 320;

/** The same syntax gate `api.functions.assignments` applies server-side. */
export function isSyntacticallyValidEmail(value: string): boolean {
  if (value.length > ASSIGNEE_EMAIL_MAX_LENGTH) {
    return false;
  }
  const atIndex = value.indexOf("@");
  if (atIndex === -1 || value.includes("@", atIndex + 1)) {
    return false;
  }
  const local = value.slice(0, atIndex);
  const domain = value.slice(atIndex + 1);
  if (local.length === 0 || domain.length === 0 || /\s/.test(local) || /\s/.test(domain)) {
    return false;
  }
  return domain.slice(1, -1).includes(".");
}

/** OBJ-C-11 (FIND-1239): `String.prototype.trim()` strips only ECMAScript `WhiteSpace` and
 * `LineTerminator` code points, while the server's `str.strip()` (`api.functions.assignments`)
 * strips everything `str.isspace()` accepts -- which additionally includes NEL (U+0085) and the
 * information separators U+001C-U+001F. A name carrying one of those four at either end is
 * trimmed away server-side (so the server accepts it) while the client's `.trim()`-ed value still
 * carries it, so `DISALLOWED_CHARACTER_PATTERN` (which refuses every `Cc` control character and
 * U+0085 explicitly) refuses a name the server would in fact accept. Trimming those characters
 * first (a linear scan from each end, no regex), before any other check, and using its result as the value both checked and sent, keeps
 * the two rules' trimming in parity. */
const WHITESPACE_PATTERN = /\s/u;

function isServerStrippedWhitespace(character: string): boolean {
  const code = character.codePointAt(0) ?? 0;
  return WHITESPACE_PATTERN.test(character) || code === 0x85 || (code >= 0x1c && code <= 0x1f);
}

/** The same trimming `_validate_assignee_name` applies server-side (`raw.strip()`), applied
 * client-side so the Assign and Reassign dialogs trim, validate and submit the identical value
 * the server will see (OBJ-C-11). Used for `assigneeName` only -- the email field has its own,
 * unrelated trimming. */
export function trimAssigneeName(name: string): string {
  const characters = Array.from(name);
  let start = 0;
  let end = characters.length;
  while (start < end && isServerStrippedWhitespace(characters[start] ?? "")) {
    start += 1;
  }
  while (end > start && isServerStrippedWhitespace(characters[end - 1] ?? "")) {
    end -= 1;
  }
  return characters.slice(start, end).join("");
}

export function validateAssigneeFields(name: string, email: string, nameFieldId: string, emailFieldId: string): AssigneeFieldErrors {
  const summary: ErrorSummaryItem[] = [];
  let nameError: string | undefined;
  let emailError: string | undefined;

  const trimmedName = trimAssigneeName(name);
  if (trimmedName === "") {
    nameError = getErrorMessage("assign.assigneeName", "missing");
    summary.push({ fieldId: nameFieldId, text: nameError });
  } else if (isAssigneeNameRejectedByFormatRule(trimmedName)) {
    nameError = content.assigneeNameFormatError;
    summary.push({ fieldId: nameFieldId, text: content.assigneeNameFormatErrorSummary });
  }
  if (email.trim() === "") {
    emailError = getErrorMessage("assign.assigneeEmail", "missing");
    summary.push({ fieldId: emailFieldId, text: emailError });
  } else if (!isSyntacticallyValidEmail(email.trim())) {
    emailError = getErrorMessage("confirmDetails.contactEmail", "invalidFormat");
    summary.push({ fieldId: emailFieldId, text: getErrorMessage("assign.assigneeEmail", "invalidFormat") });
  }

  return {
    summary,
    ...(nameError !== undefined ? { nameError } : {}),
    ...(emailError !== undefined ? { emailError } : {}),
  };
}
